import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Stack,
  Alert
} from '@mui/material';
import { DataGrid, GridColDef, GridInitialState } from '@mui/x-data-grid';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { EmployeeTimeOffRegimeDetailDto } from 'axiosApi/models/employee-time-off-regime-detail-dto';
import { DeleteEmployeeTimeOffStockCommand } from 'axiosApi/models/delete-employee-time-off-stock-command';
import AddTimeOffAssignmentModal from './AddTimeOffAssignmentModal';
import AddRegimeModal from './AddRegimeModal';
import EditRegimeModal from './EditRegimeModal';
import EditTimeOffStockModal from './EditTimeOffStockModal';
import Spinner from 'components/Spinner';
import { useDialog } from 'common/DialogProvider';
import { handleAPIError } from 'common/errorHandler';
import ErrorSummary from 'components/common/ErrorSummary';

interface TimeOffData {
  id: number;
  name: string;
  annualAssignment: number;
  availableDays: number;
  validity: string;
  perks: string[];
  isIndented?: boolean;
}

interface TimeOffSectionProps {
  employeeId?: number;
  disabled?: boolean;
}

const TimeOffSection: React.FC<TimeOffSectionProps> = ({
  employeeId,
  disabled
}) => {
  const api = useApi();
  const toast = useToastMessageQueue();
  const [employeeTimeOffData, setEmployeeTimeOffData] = useState<EmployeeTimeOffRegimeDetailDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isAddRegimeModalOpen, setIsAddRegimeModalOpen] = useState<boolean>(false);
  const [isEditRegimeModalOpen, setIsEditRegimeModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [selectedStock, setSelectedStock] = useState<any>(null);
  const [errors, setErrors] = useState<any>(null);
  const { openDialog, closeDialog } = useDialog();

  useEffect(() => {
    if (employeeId && api) {
      fetchTimeOffData();
    }
  }, [employeeId, api]);

  const fetchTimeOffData = async () => {
    if (!employeeId || !api) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.timeoffApi.apiVversionEmployeeTimeOffRegimeEmployeeEmployeeIdGet(employeeId, '1');
      if (response.status === 200 && response.data?.data) {
        setEmployeeTimeOffData(response.data.data);
      } else {
        setEmployeeTimeOffData(null);
      }
    } catch (err: any) {
      setEmployeeTimeOffData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return intl.get('timeOff.common.notAvailable');
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateObj.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return intl.get('timeOff.common.notAvailable');
    }
  };

  const formatDateRange = (validFrom: Date | string | null | undefined, validUntil: Date | string | null | undefined): string => {
    const from = formatDate(validFrom);
    const until = formatDate(validUntil);
    const notAvailable = intl.get('timeOff.common.notAvailable');

    if (from === notAvailable && until === notAvailable) return notAvailable;
    if (from === notAvailable) return intl.get('timeOff.common.validUntil', { date: until });
    if (until === notAvailable) return intl.get('timeOff.common.validFrom', { date: from });
    return `${from} - ${until}`;
  };

  const transformedData: TimeOffData[] = employeeTimeOffData?.employeeTimeOffStocks
    ? employeeTimeOffData.employeeTimeOffStocks.map((stock) => {
        const regimeType = employeeTimeOffData.employeeRegimeTimeOffTypes?.find(
          rt => rt.id === stock.idEmployeeRegimeTimeOffType
        );
        const timeOffType = regimeType?.timeOffType;
        if (!timeOffType) return null;

        const name = stock.name || intl.get('timeOff.common.unknown');
        const validity = formatDateRange(stock.validFrom, stock.validUntil);
        const totalDays = stock.totalStock || 0;
        const availableDays = stock.availableStock || 0;

        const perks: string[] = [];
        if (timeOffType.isBillable) {
          perks.push(intl.get('timeOff.perks.billable'));
        }
        if (timeOffType.isCumulative) {
          perks.push(intl.get('timeOff.perks.cumulative'));
        }
        if (timeOffType.requiresSpecialApproval) {
          perks.push(intl.get('timeOff.perks.specialApproval'));
        }
        if (timeOffType.allowNegativeBalance) {
          perks.push(intl.get('timeOff.perks.negativeBalance'));
        }

        return {
          id: stock.id || 0,
          name,
          annualAssignment: totalDays,
          availableDays: availableDays,
          validity,
          perks,
          isIndented: false
        };
      }).filter(Boolean) as TimeOffData[]
    : [];

  const handleAddAssignment = () => {
    setIsAddModalOpen(true);
  };

  const handleEditAssignment = () => {
    setIsEditRegimeModalOpen(true);
  };

  const handleAddRegime = () => {
    setIsAddRegimeModalOpen(true);
  };

  const handleEdit = (id: number) => {
    const stock = employeeTimeOffData?.employeeTimeOffStocks?.find(s => s.id === id);
    if (stock) {
      setSelectedStock(stock);
      setIsEditModalOpen(true);
    }
  };

  const handleDelete = async (id: number) => {
    if (!api) return;

    try {
      const stock = employeeTimeOffData?.employeeTimeOffStocks?.find(s => s.id === id);
      if (!stock) {
        toast.error({ message: intl.get('timeOff.messages.stockNotFound') });
        return;
      }

      const deleteCommand: DeleteEmployeeTimeOffStockCommand = {
        id: stock.id,
        idEmployeeRegimeTimeOffType: stock.idEmployeeRegimeTimeOffType,
        idEmployeeTimeOffRegime: employeeTimeOffData?.id,
        userEntityId: undefined
      };

    const openDialogId = openDialog({
      title: intl.get("delete.modal.title"),
      message: intl.get("delete.modal.stockRow.message"),
      type: 'confirm',
      confirmButton: {
          confirmLabel: intl.get("delete.modal.delete.button"),
          handleConfirm: async () => {
            await api.timeoffApi.apiVversionEmployeeTimeOffStockDelete("1", deleteCommand).then((response) => {
            if (response.status === 200) {
              toast.success({ message: intl.get('timeOff.messages.deleteSuccess') });
              closeDialog(openDialogId);
              fetchTimeOffData();
            } else {
              toast.error({ message: intl.get('timeOff.messages.deleteError') });
              closeDialog(openDialogId);
            }
          }).catch((error) => {
            toast.error({ 
              message: intl.get(error.response?.data?.errors[0]?.errorLabelKey) 
            });
            handleAPIError(error.response.data.errors[0], toast, errors);
            setErrors({ ...errors });
            closeDialog(openDialogId);
          });
          }
        },
      cancelButton: {
        cancelLabel: intl.get("delete.modal.cancel.button"),
        handleCancel: () => {
          closeDialog(openDialogId);
        },
      },
      });
    } catch (error: any) {
      console.error('Error deleting time off stock:', error);
      toast.error({
        message: error.response?.data?.message || intl.get('timeOff.messages.deleteError') 
      });
    }
  };

  const handleDeleteRegime = async () => {
    if (!api || !employeeTimeOffData?.id) return;

    const openDialogId = openDialog({
      title: intl.get("delete.modal.title"),
      message: intl.get("warning.employeeregime.delete"),
      type: 'confirm',
      confirmButton: {
        confirmLabel: intl.get("delete.modal.delete.button"),
        handleConfirm: async () => {
          try {
            const response = await api.timeoffApi.apiVversionEmployeeTimeOffRegimeIdDelete(employeeTimeOffData.id!, "1");
            if (response.status === 200) {
              toast.success({ message: intl.get('timeOff.messages.regimeDeleteSuccess') });
              closeDialog(openDialogId);
              fetchTimeOffData();
            } 
          } catch (error: any) {
            toast.error({ 
              message: intl.get(error.response?.data?.errors[0]?.errorLabelKey) || intl.get('timeOff.messages.regimeDeleteError')
            });
            handleAPIError(error.response?.data?.errors[0], toast, errors);
            setErrors({ ...errors });
            closeDialog(openDialogId);
          }
          
        }
      },
      cancelButton: {
        cancelLabel: intl.get("delete.modal.cancel.button"),
        handleCancel: () => {
          closeDialog(openDialogId);
        },
      },
    });
  };

  const getPerkChipColor = (perk: string) => {
    switch (perk) {
      case intl.get('timeOff.perks.billable'):
        return 'primary';
      case intl.get('timeOff.perks.cumulative'):
        return 'secondary';
      case intl.get('timeOff.perks.specialApproval'):
        return 'warning';
      case intl.get('timeOff.perks.negativeBalance'):
        return 'error';
      case intl.get('timeOff.perks.active'):
        return 'success';
      case intl.get('timeOff.perks.inactive'):
        return 'error';
      default:
        return 'default';
    }
  };

  const hasRegime = employeeTimeOffData?.timeOffRegime != null;

  const INITIAL_STATE: GridInitialState = {
    sorting: {
      sortModel: [
        {
          field: 'name',
          sort: 'asc',
        },
      ],
    },
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: intl.get('timeOff.table.name'),
      width: 150,
      flex: 1,
    },
    {
      field: 'annualAssignment',
      headerName: intl.get('timeOff.table.annualAssignment'),
      width: 130,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'availableDays',
      headerName: intl.get('timeOff.table.availableDays'),
      width: 130,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'validity',
      headerName: intl.get('timeOff.table.validity'),
      width: 200,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'perks',
      headerName: intl.get('timeOff.table.perks'),
      width: 400,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box justifyContent="center" gap={1}>
          {params.value.map((perk: string, index: number) => (
            <Chip
              key={index}
              label={perk}
              size="small"
              color={getPerkChipColor(perk) as any}
              variant="outlined"
            />
          ))}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 75,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" justifyContent="center" gap={0.5}>
          <IconButton
            size="small"
            disabled={disabled}
            onClick={() => handleEdit(params.row.id)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            disabled={disabled}
            onClick={() => handleDelete(params.row.id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <FlightTakeoffIcon color="primary" />
          <ErrorSummary errors={errors} showEverything={true} mode="warning" />
          <Typography variant="h6" component="h3" color="primary">
              {intl.get('timeOff.title')}
            </Typography>
        </Box>
      </Box>

      { isLoading ? (
        <Box display="flex" justifyContent="center">
          <Spinner small={true} />
        </Box>
      ) : (
        <>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {hasRegime ? (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" component="h3" color="primary">
              {employeeTimeOffData?.timeOffRegime?.name}
            </Typography>
            <Stack direction="row" spacing={1}>
              
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddAssignment}
              size="small"
              disabled={disabled}
            >
              <AddIcon />
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleEditAssignment}
              size="small"
              disabled={disabled}
            >
              <FlightTakeoffIcon />
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteRegime}
              size="small"
              disabled={disabled}
            >
              <DeleteIcon />
            </Button>
            </Stack>
          </Box>

          <Box style={{ width: '100%', display: 'flex' }}>
            {transformedData.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8,
                  borderRadius: 2,
                  width: '100%'
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {intl.get('timeOff.messages.noData')}
                </Typography>
              </Box>
            ) : (
              <DataGrid
                rows={transformedData}
                columns={columns}
                rowCount={transformedData.length}
                loading={isLoading}
                initialState={INITIAL_STATE}
                disableRowSelectionOnClick
                hideFooterPagination={true}
                hideFooter={true}
              />
            )}
          </Box>

      {employeeId && (
        <AddTimeOffAssignmentModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          employeeRegimeId={employeeTimeOffData?.id || 0}
          onSuccess={() => {
            fetchTimeOffData();
          }}
        />
      )}
      </>
      ) : (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary" mb={2}>
            {intl.get('timeOff.messages.noRegimeAssigned')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            disabled={disabled}
            onClick={handleAddRegime}
            size="small"
          >
            {intl.get('timeOff.actions.assignRegime')}
          </Button>
        </Box>
      )}

      {employeeId && (
        <AddRegimeModal
          isOpen={isAddRegimeModalOpen}
          onClose={() => setIsAddRegimeModalOpen(false)}
          employeeId={employeeId}
          onSuccess={() => {
            fetchTimeOffData();
          }}
        />
      )}

      <EditTimeOffStockModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedStock(null);
        }}
        stock={selectedStock}
        employeeRegimeId={employeeTimeOffData?.id || 0}
        employeeTimeOffData={employeeTimeOffData}
        onSuccess={() => {
          fetchTimeOffData();
        }}
      />

      {employeeId && (
        <EditRegimeModal
          isOpen={isEditRegimeModalOpen}
          onClose={() => setIsEditRegimeModalOpen(false)}
          employeeId={employeeId}
          employeeTimeOffData={employeeTimeOffData}
          onSuccess={() => {
            fetchTimeOffData();
          }}
        />
      )}
      </>
      )}
    </Box>
  );
};

export default TimeOffSection;